import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ensureCsrfToken, refreshAuthentication, resetCsrfToken, type AuthUser } from '../lib/api'
import { LoginView } from './LoginView'

interface AuthContextValue {
  user: AuthUser | null
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  const clearAuthentication = useCallback(() => {
    setUser(null)
    queryClient.clear()
  }, [queryClient])

  const updateAuthentication = useCallback((event: Event) => {
    if (event instanceof CustomEvent) setUser(event.detail as AuthUser)
  }, [])

  useEffect(() => {
    let active = true
    ensureCsrfToken()
      .then(() => api<AuthUser>('/api/v1/auth/me'))
      .then((authenticatedUser) => { if (active) setUser(authenticatedUser) })
      .catch(() => { if (active) setUser(null) })
      .finally(() => { if (active) setLoading(false) })
    window.addEventListener('mediathekview-auth-required', clearAuthentication)
    window.addEventListener('mediathekview-auth-refreshed', updateAuthentication)
    return () => {
      active = false
      window.removeEventListener('mediathekview-auth-required', clearAuthentication)
      window.removeEventListener('mediathekview-auth-refreshed', updateAuthentication)
    }
  }, [clearAuthentication, updateAuthentication])

  useEffect(() => {
    if (!user?.expiresAt) return
    const remaining = Date.parse(user.expiresAt) - Date.now()
    const delay = Math.max(30_000, Math.floor(remaining * 0.8))
    const timeout = window.setTimeout(() => {
      void refreshAuthentication()
        .then((authenticatedUser) => { if (!authenticatedUser) clearAuthentication() })
        .catch(() => { /* A later API request can retry after a transient outage. */ })
    }, delay)
    return () => window.clearTimeout(timeout)
  }, [clearAuthentication, user?.expiresAt])

  const login = useCallback(async (username: string, password: string) => {
    const authenticatedUser = await api<AuthUser>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    })
    resetCsrfToken()
    await ensureCsrfToken()
    queryClient.clear()
    setUser(authenticatedUser)
  }, [queryClient])

  const logout = useCallback(async () => {
    await api<void>('/api/v1/auth/logout', { method: 'POST' })
    resetCsrfToken()
    clearAuthentication()
  }, [clearAuthentication])

  const value = useMemo(() => ({ user, login, logout }), [user, login, logout])

  if (loading) {
    return <div className="auth-loading" role="status"><img src="/meine-mediathek-logo.png" alt="" /><span>Anmeldung wird geprüft …</span></div>
  }
  return <AuthContext.Provider value={value}>{user ? children : <LoginView onLogin={login} />}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
